import { Speed as SpeedIcon, Timer as TimerIcon } from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { BarChart, PieChart } from "@mui/x-charts";
import type { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router";
import { DataGrid } from "../../../components/data-grid";
import { theme } from "../../../theme/theme";
import type { TestRunMetrics } from "../../../types";
import { formatDuration } from "../../../utils";

interface TestRunsTabProps {
  data?: TestRunMetrics;
  isLoading?: boolean;
}

const statusColors: Record<string, string> = {
  passed: theme.palette.success.main,
  failed: theme.palette.error.main,
  pending: theme.palette.warning.main,
  running: theme.palette.info.main,
  skipped: theme.palette.grey[500],
  error: theme.palette.error.dark,
  flaky: theme.palette.warning.dark,
  cancelled: theme.palette.grey[600],
  timed_out: theme.palette.error.light,
};

const columns: GridColDef[] = [
  {
    field: "test_name",
    headerName: "Test Name",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Link
        to={`/tests/${params.row.test_id}`}
        style={{ color: theme.palette.primary.main, textDecoration: "none" }}
      >
        {params.value}
      </Link>
    ),
  },
  {
    field: "total_runs",
    headerName: "Runs",
    width: 80,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "passed",
    headerName: "Passed",
    width: 80,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Typography
        color="success.main"
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "failed",
    headerName: "Failed",
    width: 80,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Typography
        color="error.main"
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "pass_rate",
    headerName: "Pass Rate",
    width: 100,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const rate = params.value as number;
      const color = rate >= 90 ? "success" : rate >= 70 ? "warning" : ("error" as const);
      return <Chip label={`${rate.toFixed(0)}%`} size="small" color={color} />;
    },
  },
  {
    field: "avg_duration_seconds",
    headerName: "Avg Duration",
    width: 120,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value: number) => formatDuration(value),
  },
  {
    field: "last_run_at",
    headerName: "Last Run",
    width: 150,
    valueFormatter: (value) => (value ? new Date(value).toLocaleDateString() : "Never"),
  },
];

export function TestRunsTab({ data, isLoading }: TestRunsTabProps) {
  const runsByStatus = data?.runs_by_status ?? {};
  const runsByTest = data?.runs_by_test ?? [];
  const longestTests = data?.longest_tests ?? [];

  const totalRuns = Object.values(runsByStatus).reduce((sum, count) => sum + count, 0);
  const statusData = Object.entries(runsByStatus)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([status, count], index) => {
      const percentage = totalRuns > 0 ? ((count / totalRuns) * 100).toFixed(0) : 0;
      return {
        id: index,
        value: count,
        label: `${status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")} (${percentage}%)`,
        color: statusColors[status] || theme.palette.grey[500],
      };
    });

  return (
    <Stack spacing={3}>
      {/* Status Distribution and Longest Tests */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <SpeedIcon color="primary" fontSize="small" />
                <Typography variant="h6">Status Distribution</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Test runs by execution status
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={280} sx={{ mt: 2 }} />
              ) : statusData.length > 0 ? (
                <PieChart
                  series={[{ data: statusData, innerRadius: 50 }]}
                  height={280}
                  margin={{ top: 20, bottom: 20, left: 0, right: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 280,
                  }}
                >
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <TimerIcon color="warning" fontSize="small" />
                <Typography variant="h6">Longest Running Tests</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Top 10 tests by average execution time
              </Typography>
              <BarChart
                layout="horizontal"
                series={[
                  {
                    data: longestTests.map((t) => t.avg_duration_seconds),
                    color: theme.palette.warning.main,
                    label: "Avg Duration",
                    valueFormatter: (value) => (value !== null ? formatDuration(value) : ""),
                  },
                ]}
                xAxis={[
                  {
                    valueFormatter: (value) => formatDuration(value as number),
                  },
                ]}
                yAxis={[
                  {
                    data: longestTests.map((t) => t.test_name),
                    scaleType: "band",
                  },
                ]}
                height={280}
                margin={{ left: 10 }}
                loading={isLoading}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tests Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Run Summary
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Per-test execution statistics for the selected period
          </Typography>
          <DataGrid
            rows={runsByTest.map((r, idx) => ({
              ...r,
              id: r.test_id || idx,
            }))}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: "total_runs", sort: "desc" }] },
            }}
            minHeight="500px"
            maxHeight="800px"
          />
        </CardContent>
      </Card>
    </Stack>
  );
}
